import { CheckIcon } from 'lucide-react'
import React from 'react'

import { cn } from './cn'

interface CheckboxInputProps {
  className?: string
  boxClassName?: string
  label?: string | React.ReactNode
  name?: string
  value?: string
  checked?: boolean
  defaultChecked?: boolean
  disabled?: boolean
  variant?: 'default' | 'inverted'
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

/**
 * Ship&Play custom checkbox. Uses a visually-hidden `<input type="checkbox">`
 * with a styled sibling `<span>` for the box. Ported verbatim from playground
 * so LeadCapture (and any future consumer) renders identically.
 */
export const CheckboxInput = ({
  className,
  boxClassName,
  label,
  name,
  value,
  checked,
  defaultChecked,
  disabled,
  variant = 'default',
  onChange,
}: CheckboxInputProps): React.ReactElement => {
  // Only bind `checked` when a change handler comes with it, otherwise the
  // input would be frozen by React. Without it the checkbox stays
  // uncontrolled and toggles on its own.
  const isControlled = checked !== undefined && onChange !== undefined

  return (
    <label
      className={cn(
        'snplay:group snplay-can-focus-within snplay:relative snplay:flex',
        'snplay:cursor-pointer snplay:items-start snplay:gap-3 snplay:py-2',
        disabled && 'snplay:cursor-not-allowed snplay:opacity-60',
        className,
      )}
    >
      <input
        type="checkbox"
        className="snplay:peer snplay:sr-only"
        name={name}
        value={value}
        disabled={disabled}
        onChange={onChange}
        {...(isControlled ? { checked } : { defaultChecked: defaultChecked ?? checked })}
      />
      <span
        className={cn(
          'snplay:mt-0.5 snplay:flex snplay:size-6 snplay:shrink-0',
          'snplay:items-center snplay:justify-center snplay:rounded-lg',
          'snplay:text-transparent snplay:transition-colors',
          variant === 'default' &&
            'snplay:border snplay:border-ui-decoratorMedium snplay:bg-background' +
              ' snplay:group-hover:border-ui-primary',
          variant === 'default' &&
            'snplay:peer-checked:border-ui-primary snplay:peer-checked:bg-ui-primary' +
              ' snplay:peer-checked:text-ui-foreground',
          variant === 'inverted' &&
            'snplay:border-ui-transparent snplay:border snplay:bg-background' +
              ' snplay:group-hover:border-primary',
          variant === 'inverted' &&
            'snplay:peer-checked:border-primary snplay:peer-checked:bg-primary' +
              ' snplay:peer-checked:text-ui-foreground',
          boxClassName,
        )}
      >
        <CheckIcon aria-hidden="true" className="snplay:size-5 snplay:stroke-2" />
      </span>
      <div className="snplay:text-base">{label}</div>
    </label>
  )
}
